/**
 * CONF-106 forward pricing engine (Epic F slice 1).
 * DiscountPrice = Σ(componentCost × qty) × (1 + integrationPct), rounded
 * HALF_EVEN exactly once per line, in cents. List is derived UPWARD from the
 * rounded DiscountPrice (list = discount / (1 - discountPct)); DiscountPrice
 * is authoritative (spec §6). All inputs integer µ$ / basis points.
 */
import { mulDivHalfEven, dotDivHalfEven, assertSafeInt } from './money';

export interface CatalogSnapshot {
  vendor: string;
  quarter: string;
  componentCostMicros: Record<string, number>;
  integrationBp: number;                        // global — strictly, no per-SKU int%
  hwDiscountBp: number;                         // global HW discount %
  discountBpOverrides: Record<string, number>;  // per-SKU override wins if present
  compositions: Record<string, Array<{ componentSku: string; qty: number }>>;
  // Component SKUs exempt from the integration uplift (ratified cost model:
  // labor/chassis are flat adds, not marked up) — pass through 1:1 into the price.
  upliftExemptSkus: Set<string>;
  /**
   * The transaction-time cutoff this snapshot was resolved at, or undefined when
   * resolved without one.
   *
   * NOT the cutoff to reprice against. Reprice diffs the basis against the LIVE
   * edition's asOf; using the basis's own would diff it against itself and show
   * nothing (see repriceCandidate.ts). The name is deliberately not `asOf` so
   * that mistake reads wrong at the call site.
   *
   * OPTIONAL FOREVER: basisSnapshot is persisted on saved quotes, and quotes
   * written before this field existed must hydrate unchanged.
   */
  resolvedAsOf?: string;
}

export interface SkuPrice {
  productSku: string;
  rollupMicros: number;
  discountCents: number;
  listCents: number;
  discountBp: number;
  integrationBp: number;
}

/** The uplifted/exempt split of a product's component rollup, in µ$.
 *  Uplift-exempt components (labor/chassis) are flat adds, not marked up. */
export function rollupSplit(
  productSku: string, snap: CatalogSnapshot,
): { upliftedMicros: number; exemptMicros: number } {
  const composition = snap.compositions[productSku];
  if (!composition) throw new Error(`no composition for SKU ${productSku}`);
  let upliftedMicros = 0, exemptMicros = 0;
  for (const { componentSku, qty } of composition) {
    const cost = snap.componentCostMicros[componentSku];
    if (cost === undefined) throw new Error(`missing component cost: ${componentSku}`);
    assertSafeInt(qty, `qty of ${componentSku}`);
    const amount = cost * qty;
    if (snap.upliftExemptSkus.has(componentSku)) {
      exemptMicros += amount;
      assertSafeInt(exemptMicros, `exempt rollup of ${productSku}`);
    } else {
      upliftedMicros += amount;
      assertSafeInt(upliftedMicros, `uplifted rollup of ${productSku}`);
    }
  }
  return { upliftedMicros, exemptMicros };
}

export function priceSku(productSku: string, snap: CatalogSnapshot): SkuPrice {
  const composition = snap.compositions[productSku];
  if (!composition) throw new Error(`no composition for SKU ${productSku}`);
  // Layered lookup (spec §5): per-SKU override wins, else the global HW discount.
  const discountBp = snap.discountBpOverrides[productSku] ?? snap.hwDiscountBp;
  if (discountBp === undefined) throw new Error(`no discount % for SKU ${productSku}`);

  const { upliftedMicros, exemptMicros } = rollupSplit(productSku, snap);
  const rollupMicros = upliftedMicros + exemptMicros;
  assertSafeInt(rollupMicros, `rollup of ${productSku}`);

  // THE one rounding point: uplifted µ$ × (1+markup) + exempt µ$ (flat) -> cents, HALF_EVEN.
  // dotDivHalfEven (part-3 consolidation) keeps the two-term numerator in
  // BigInt throughout — no Number-space product of upliftedMicros*(10000+bp).
  const discountCents = dotDivHalfEven(
    [[upliftedMicros, 10000 + snap.integrationBp], [exemptMicros, 10000]],
    100000000,
  );
  const listCents = mulDivHalfEven(discountCents, 10000, 10000 - discountBp);

  return { productSku, rollupMicros, discountCents, listCents, discountBp,
           integrationBp: snap.integrationBp };
}

export function deriveHwPricing(snap: CatalogSnapshot): Record<string, SkuPrice> {
  const sheet: Record<string, SkuPrice> = {};
  for (const sku of Object.keys(snap.compositions)) sheet[sku] = priceSku(sku, snap);
  return sheet;
}
